import { useRef, useMemo, Component } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { Sparkles, MeshDistortMaterial, Float } from "@react-three/drei";
import * as THREE from "three";

// Premium static fallback when WebGL cannot initialise (no-GPU / blocked contexts).
const HeroFallback = () => (
  <div
    className="absolute inset-0"
    data-testid="hero-canvas-fallback"
    style={{
      background:
        "radial-gradient(circle at 65% 45%, rgba(197,160,89,0.28), transparent 55%), radial-gradient(circle at 30% 70%, rgba(124,92,255,0.12), transparent 50%), #0A0A0A",
    }}
  />
);

// Error boundary — catches THREE.WebGLRenderer failures and shows the fallback.
class WebGLBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }
  static getDerivedStateFromError() {
    return { failed: true };
  }
  componentDidCatch() {}
  render() {
    if (this.state.failed) return this.props.fallback;
    return this.props.children;
  }
}

// Liquid-gold core — a slowly morphing, polished metallic orb.
const LiquidCore = () => {
  const ref = useRef();
  useFrame((_, d) => {
    if (!ref.current) return;
    ref.current.rotation.y += d * 0.14;
    ref.current.rotation.x += d * 0.045;
  });
  return (
    <Float speed={1.1} rotationIntensity={0.35} floatIntensity={0.9}>
      <mesh ref={ref}>
        <icosahedronGeometry args={[1.55, 16]} />
        <MeshDistortMaterial
          color="#C5A059"
          metalness={1}
          roughness={0.2}
          distort={0.34}
          speed={1.5}
          emissive="#4a340f"
          emissiveIntensity={0.3}
        />
      </mesh>
    </Float>
  );
};

// Fine champagne wireframe ring orbiting the core — high-tech accent.
const WireRing = ({ radius = 2.6, tilt = 1.15, speed = 0.08 }) => {
  const ref = useRef();
  useFrame((_, d) => {
    if (ref.current) ref.current.rotation.z += d * speed;
  });
  return (
    <mesh ref={ref} rotation={[tilt, 0, 0]}>
      <torusGeometry args={[radius, 0.008, 16, 220]} />
      <meshBasicMaterial color="#C5A059" transparent opacity={0.5} />
    </mesh>
  );
};

const DustField = ({ count = 900 }) => {
  const ref = useRef();
  const positions = useMemo(() => {
    const arr = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      const r = 4 + Math.random() * 6;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      arr[i * 3] = r * Math.sin(phi) * Math.cos(theta);
      arr[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
      arr[i * 3 + 2] = r * Math.cos(phi);
    }
    return arr;
  }, [count]);
  useFrame((_, d) => {
    if (ref.current) ref.current.rotation.y += d * 0.015;
  });
  return (
    <points ref={ref}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" args={[positions, 3]} />
      </bufferGeometry>
      <pointsMaterial size={0.018} color="#F4EFE6" transparent opacity={0.4} sizeAttenuation depthWrite={false} />
    </points>
  );
};

export const HeroCanvas = () => {
  return (
    <WebGLBoundary fallback={<HeroFallback />}>
      <div className="absolute inset-0" data-testid="hero-canvas">
        <Canvas camera={{ position: [0, 0, 6], fov: 42 }} dpr={[1, 2]} gl={{ alpha: true, antialias: true }}>
          <ambientLight intensity={0.45} />
          <directionalLight position={[4, 5, 3]} intensity={2.4} color="#fff2d4" />
          <pointLight position={[-4, -2, 2]} intensity={1.6} color="#C5A059" />
          <pointLight position={[0, 3, -3]} intensity={1.1} color="#7c5cff" />
          <LiquidCore />
          <WireRing radius={2.55} tilt={1.15} speed={0.08} />
          <WireRing radius={3.15} tilt={-0.7} speed={-0.05} />
          <DustField />
          <Sparkles count={120} scale={12} size={2.4} speed={0.25} opacity={0.55} color="#C5A059" />
        </Canvas>
      </div>
    </WebGLBoundary>
  );
};

export default HeroCanvas;
